// SVG-Layer im Worldspace fuer Links. Verbindet From→To-Mittelpunkte.
// Optional O4: Click auf Pfad selektiert den Link (Inspector zeigt Type-Dropdown).
// Delete-Taste mit selektiertem Link → loescht ihn (Canvas-Handler).

import type { CanvasObject, Link, LinkRouting, LinkType } from "@ms-whiteboard/shared";
import { useMemo } from "react";
import { objectBounds } from "./object-bounds.js";
import { buildLinkPath } from "./link-path.js";

export interface PendingLink {
  fromPoint: { x: number; y: number };
  currentPoint: { x: number; y: number };
}

interface LinkLayerProps {
  links: readonly Link[];
  objectsById: ReadonlyMap<string, CanvasObject>;
  zoom: number;
  onSelectLink: (id: string) => void;
  selectedLinkId: string | null;
  pendingLink?: PendingLink | null;
}

const TYPE_STROKE: Record<LinkType, string> = {
  "relates-to": "#4a4a4a",
  resolves: "#16a34a",
  "depends-on": "#dc2626",
  "tree-parent": "#cbd5e1",
  "sequence-flow": "#1f2937",
};

interface ObjectGeom {
  cx: number;
  cy: number;
  hw: number;
  hh: number;
}

function objectGeom(obj: CanvasObject): ObjectGeom {
  const b = objectBounds(obj);
  return {
    cx: obj.x + b.w / 2,
    cy: obj.y + b.h / 2,
    hw: b.w / 2,
    hh: b.h / 2,
  };
}

// Schnittpunkt einer Linie aus der Mitte des Rechtecks mit der Rechteck-Kante.
// Resultat: Distanz t entlang (ux, uy) bis zur Kante. Min(hw/|ux|, hh/|uy|).
function edgeOffset(g: ObjectGeom, ux: number, uy: number): { dx: number; dy: number } {
  const tx = Math.abs(ux) > 1e-6 ? g.hw / Math.abs(ux) : Number.POSITIVE_INFINITY;
  const ty = Math.abs(uy) > 1e-6 ? g.hh / Math.abs(uy) : Number.POSITIVE_INFINITY;
  const t = Math.min(tx, ty);
  return { dx: ux * t, dy: uy * t };
}

export function LinkLayer({
  links,
  objectsById,
  zoom,
  onSelectLink,
  selectedLinkId,
  pendingLink = null,
}: LinkLayerProps): JSX.Element | null {
  const items = useMemo(() => {
    return links
      .map((link) => {
        const a = objectsById.get(link.from);
        const b = objectsById.get(link.to);
        if (!a || !b) return null;
        const ga = objectGeom(a);
        const gb = objectGeom(b);
        // tree-parent: Bezier-Anker auf der linken/rechten Pill-Kante, nicht ueber
        // edge-Vektor — sonst startet die Kurve mittig im Pill und faedelt nicht
        // sauber zwischen Geschwistern durch.
        if (link.type === "tree-parent") {
          const targetIsRight = gb.cx >= ga.cx;
          const startX = ga.cx + (targetIsRight ? ga.hw : -ga.hw);
          const startY = ga.cy;
          const endX = gb.cx + (targetIsRight ? -gb.hw : gb.hw);
          const endY = gb.cy;
          return { link, startX, startY, endX, endY, isTree: true };
        }
        const dx = gb.cx - ga.cx;
        const dy = gb.cy - ga.cy;
        const len = Math.hypot(dx, dy);
        if (len < 1) return null;
        const ux = dx / len;
        const uy = dy / len;
        const aOff = edgeOffset(ga, ux, uy);
        const bOff = edgeOffset(gb, ux, uy);
        const startX = ga.cx + aOff.dx;
        const startY = ga.cy + aOff.dy;
        const endX = gb.cx - bOff.dx;
        const endY = gb.cy - bOff.dy;
        return { link, startX, startY, endX, endY, isTree: false };
      })
      .filter((v): v is NonNullable<typeof v> => v !== null);
  }, [links, objectsById]);

  if (items.length === 0 && !pendingLink) return null;

  // SVG inverse-scaled fuer zoom-invariante Strichbreite.
  const strokeWidth = Math.max(1.2, 1.5 / zoom);
  const markerScale = Math.max(8, 8 / zoom);

  return (
    <svg
      className="canvas-link-layer"
      // Worldspace-SVG: deckt einen sehr grossen Bereich, Browser clipt.
      style={{
        position: "absolute",
        left: -50000,
        top: -50000,
        width: 100000,
        height: 100000,
        pointerEvents: "none",
      }}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Connectors"
    >
      <title>Connectors</title>
      <defs>
        <marker
          id="link-arrow"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth={markerScale}
          markerHeight={markerScale}
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#4a4a4a" />
        </marker>
      </defs>
      {pendingLink && (
        <line
          className="canvas-pending-link"
          x1={pendingLink.fromPoint.x + 50000}
          y1={pendingLink.fromPoint.y + 50000}
          x2={pendingLink.currentPoint.x + 50000}
          y2={pendingLink.currentPoint.y + 50000}
          stroke="#ea5b0b"
          strokeWidth={Math.max(1.6, 2 / zoom)}
          strokeDasharray={`${Math.max(6, 6 / zoom)} ${Math.max(4, 4 / zoom)}`}
          markerEnd="url(#link-arrow)"
          style={{ pointerEvents: "none" }}
        />
      )}
      {items.map(({ link, startX, startY, endX, endY, isTree }) => {
        const isSelected = selectedLinkId === link.id;
        const stroke = TYPE_STROKE[link.type];
        const sx = startX + 50000;
        const sy = startY + 50000;
        const ex = endX + 50000;
        const ey = endY + 50000;
        // tree-parent rendert immer als Bezier (Mindmap-Optik); sonst der pro Pfeil
        // gewaehlte Verlauf (Default gerade). Tree-Edges tragen keinen Pfeilkopf.
        const routing: LinkRouting = isTree ? "curved" : (link.routing ?? "straight");
        const d = buildLinkPath(routing, sx, sy, ex, ey);
        const width = isSelected
          ? strokeWidth * 2.2
          : link.type === "sequence-flow"
            ? strokeWidth * 1.3
            : strokeWidth;
        return (
          <g key={link.id} className={`canvas-link canvas-link-${link.type}`}>
            {/* Unsichtbarer breiter Hit-Path fuer Klick. */}
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: Links sind SVG-Elemente — Tastaturpfad via Selection + Delete-Key */}
            <path
              d={d}
              fill="none"
              stroke="transparent"
              strokeWidth={12 / zoom}
              style={{ pointerEvents: "stroke", cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                onSelectLink(link.id);
              }}
            />
            <path
              d={d}
              fill="none"
              stroke={stroke}
              strokeWidth={width}
              markerEnd={isTree ? undefined : "url(#link-arrow)"}
              style={{ pointerEvents: "none" }}
            />
          </g>
        );
      })}
    </svg>
  );
}
